import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, computed, onMounted, watch } from "vue";
  import store from "@/store";
  import router from "@/router";
  import {
    ImagesHouseTypes,
    ImageHouseLabels,
  } from "@/enums/BuilderHouseImages";
  import {
    Floorplan,
    Inclusions,
    HouseFeature,
  } from "@/types/HouseDesignInterface";
  import { ImageToRender } from "@/types/HouseCreateImageToRender";
  import { useToast } from "vue-toastification";
  import { required, minValue, maxValue } from "@vuelidate/validators";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import useVuelidate from "@vuelidate/core";
  import VuePictureCropper, { cropper } from "vue-picture-cropper";
  import ImageCropperModal from "@/components/ui/modals/ImageCropperModal.vue";
  import { ImageToUpload } from "@/types/BuilderImageToUpload";
  import {
    validImagesColorInputs,
    validsImagesNameInputs,
    validImagesDescriptionInputs,
    validsMultipleImagesInputs,
  } from "@/constants/adminHouseInputs";
  import ListItemGenerator from "@/components/builder/ListItemGenerator.vue";
  import { formatPrice } from "@/helpers/transformation/price";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import { v4 as uuidv4 } from "uuid";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import {
    formatPriceTrim,
    updatePriceInput,
  } from "@/helpers/transformation/price";
  import { AssetGalleryFormType } from "@/enums/AssetGalleryFormType";
  import {
    ColorSchemeInterface,
    CreateInternalColorSchemeInterface,
    ExternalColorSchemeInterface,
    InternalColorSchemeInterface,
  } from "@/types/ColorSchemeInterface";
  import { ColorSchemeType } from "@/enums/ColorSchemeType";
  import {
    CreateInclusionInterface,
    InclusionInterface,
  } from "@/types/InclusionInterface";
  import {
    CreateOptionalUpgradeInterface,
    OptionalUpgrade,
  } from "@/types/OptionalUpgradeInterface";
  import { InclusionCategory } from "@/enums/InclusionCategory";
  import AddAndUpdateAssetInGalleryModal from "@/components/ui/modals/AddAndUpdateAssetInGalleryModal.vue";
  import AddAndUpdateInclusionModal from "@/components/ui/modals/AddAndUpdateInclusionModal.vue";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminBuilderCreateHouse',
  setup(__props, { expose: __expose }) {
  __expose();

  const imageToUpload = ref<ImageToUpload>({
    id: "",
    data: null,
    type: "" as ImagesHouseTypes,
  });
  const isCropperModalOpen = ref<boolean>(false);
  const toast = useToast();

  const facades = ref<FacadeInterface[]>([]);
  const filteredFacades = computed(() => {
    return facades.value.filter((facade) => facade.storeys === form.storey);
  });

  const externalColorSchemes = ref<ExternalColorSchemeInterface[]>([]);
  const internalColorSchemes = ref<InternalColorSchemeInterface[]>([]);
  const inclusions = ref<InclusionInterface[]>([]);
  const optionalUpgrades = ref<OptionalUpgrade[]>([]);
  const houseFeatures = ref<HouseFeature[]>([]);
  const newFeature = ref("");

  const loading = ref(false);
  const isUploadingImage = ref(false);
  const isUploadingInclusion = ref(false);
  const inclusionName = ref("");
  const isInternalSchemeModalOpen = ref(false);
  const isInclusionModalOpen = ref(false);
  const isOptionalUpgradeModalOpen = ref(false);

  const getEmptyImage = () => {
    return [{ id: uuidv4(), url: "" }];
  };

  const initMainImage = getEmptyImage();
  const initFloorplans = getEmptyImage();
  const initLounges = getEmptyImage();
  const initKitchens = getEmptyImage();
  const initBathrooms = getEmptyImage();

  const imagesToRender = ref<ImageToRender[]>([
    {
      type: ImagesHouseTypes.MAIN_IMAGE,
      data: initMainImage,
      label: ImageHouseLabels.MAIN_IMAGE,
    },
    {
      type: ImagesHouseTypes.FLOORPLANS,
      data: initFloorplans,
      label: ImageHouseLabels.FLOORPLANS,
    },
  ]);

  const formFields = {
    facades: [] as string[],
    externalColorSchemes: [] as string[],
    internalColorSchemes: [] as string[],
    floorplans: initFloorplans as Record<string, string>[],
    lounges: initLounges as Record<string, string>[],
    kitchens: initKitchens as Record<string, string>[],
    bathrooms: initBathrooms as Record<string, string>[],
    inclusions: [] as Inclusions[],
    features: [] as string[],
    mainImages: initMainImage as Record<string, string>[],
    name: "",
    description: "",
    area: null,
    length: null,
    width: null,
    bathroom: null,
    bedroom: null,
    garage: null,
    storey: null,
    loungeRoom: null,
    basePrice: null,
    inclusionPrices: {} as Record<string, number>,
    optionalUpgrades: [] as OptionalUpgrade[],
    categorizedInclusions: [] as {
      category: InclusionCategory;
      items: string[];
    }[],
  };

  const form = reactive(Object.assign({}, formFields));

  const inclusionCategories = [
    { key: InclusionCategory.INDOOR, label: "Indoor Inclusions" },
    { key: InclusionCategory.KITCHEN, label: "Kitchen" },
    { key: InclusionCategory.BATHROOM_LAUNDRY, label: "Bathroom & Laundry" },
    { key: InclusionCategory.OUTDOOR, label: "Outdoor" },
  ];

  const categorizedInclusionsMap = reactive<
    Record<InclusionCategory, string[]>
  >({
    [InclusionCategory.INDOOR]: [],
    [InclusionCategory.KITCHEN]: [],
    [InclusionCategory.BATHROOM_LAUNDRY]: [],
    [InclusionCategory.OUTDOOR]: [],
  });

  const rules = {
    facades: { required },
    externalColorSchemes: {},
    internalColorSchemes: {},
    floorplans: { required },
    features: {},
    lounges: {},
    kitchens: {},
    bathrooms: {},
    inclusions: {},
    name: { required },
    area: { required, minValue: minValue(1) },
    length: { required, minValue: minValue(1) },
    width: { required, minValue: minValue(1) },
    bathroom: { required, minValue: minValue(1) },
    bedroom: { required, minValue: minValue(1) },
    garage: { required, minValue: minValue(1) },
    storey: { required, minValue: minValue(1), maxValue: maxValue(2) },
    loungeRoom: { required, minValue: minValue(1) },
    basePrice: {},
    mainImages: {},
    optionalUpgrades: {},
  };

  const v$ = useVuelidate(rules, form);

  const builderId = computed(() => {
    return router.currentRoute.value.params.builderId;
  });

  const updateFacades = (facade: FacadeInterface) => {
    const foundFacade = form.facades.includes(facade._id);

    if (!foundFacade) form.facades.push(facade._id);
    else form.facades = form.facades.filter((f) => f !== facade._id);
  };

  const updateExternalColorSchemes = (
    colorScheme: ExternalColorSchemeInterface
  ) => {
    const foundExternalColor = form.externalColorSchemes.includes(
      colorScheme._id
    );

    if (!foundExternalColor) form.externalColorSchemes.push(colorScheme._id);
    else
      form.externalColorSchemes = form.externalColorSchemes.filter(
        (f) => f !== colorScheme._id
      );
  };

  const updateInternalColorSchemes = (
    colorScheme: InternalColorSchemeInterface
  ) => {
    const foundInternalColor = form.internalColorSchemes.includes(
      colorScheme._id
    );

    if (!foundInternalColor) form.internalColorSchemes.push(colorScheme._id);
    else
      form.internalColorSchemes = form.internalColorSchemes.filter(
        (f) => f !== colorScheme._id
      );
  };

  const updateInclusions = (inclusion: InclusionInterface) => {
    const foundInclusion = form.inclusions.includes(inclusion._id);

    if (!foundInclusion) {
      form.inclusions.push(inclusion._id);
      form.inclusionPrices[inclusion._id] = 0;
    } else {
      form.inclusions = form.inclusions.filter((i) => i !== inclusion._id);
      delete form.inclusionPrices[inclusion._id];
    }
  };
  const showUploadButton = (type: ImagesHouseTypes, index: number) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      if (form.mainImages[0]?.url === "") {
        return true;
      }

      return index === form.mainImages.length - 1;
    }

    if (type === ImagesHouseTypes.FLOORPLANS) {
      return form.floorplans[0]?.url === "";
    }

    return true;
  };
  const updateOptionalUpgrades = (optionalUpgrade: OptionalUpgrade) => {
    const isChecked = form.optionalUpgrades.some(
      (o) => o._id === optionalUpgrade._id
    );

    const addOptionalUpgrade = () => {
      form.optionalUpgrades.push(optionalUpgrade);
    };

    const removeOptionalUpgrade = () => {
      form.optionalUpgrades = form.optionalUpgrades.filter(
        (o) => o._id !== optionalUpgrade._id
      );
    };

    if (!isChecked) {
      addOptionalUpgrade();
    } else {
      removeOptionalUpgrade();
    }
  };

  const handleCreateInternalColorScheme = async (
    dto: CreateInternalColorSchemeInterface
  ) => {
    try {
      const created = await store.dispatch(
        "colorScheme/createColorScheme",
        dto
      );

      if (created && created.type === ColorSchemeType.INTERNAL) {
        internalColorSchemes.value.push(
          created as InternalColorSchemeInterface
        );
      }
    } catch (error) {
      toast.error("An error occurred while creating the internal color scheme");
    } finally {
      isInternalSchemeModalOpen.value = false;
    }
  };

  const handleCreateInclusion = async (dto: CreateInclusionInterface) => {
    try {
      const created = await store.dispatch("inclusion/createInclusion", dto);

      if (created) {
        inclusions.value.push(created as InclusionInterface);
      }
    } catch (error) {
      toast.error("An error occurred while creating the inclusion");
    } finally {
      isInclusionModalOpen.value = false;
    }
  };

  const handleCreateOptionalUpgrade = async (
    dto: CreateOptionalUpgradeInterface
  ) => {
    try {
      const created = await store.dispatch(
        "optionalUpgrade/createOptionalUpgrade",
        dto
      );

      if (created) {
        optionalUpgrades.value.push(created as OptionalUpgrade);
      }
    } catch (error) {
      toast.error("An error occurred while creating the optional upgrade");
    } finally {
      isOptionalUpgradeModalOpen.value = false;
    }
  };

  const updateCategorizedInclusions = (
    category: InclusionCategory,
    items: { text: string }[]
  ) => {
    categorizedInclusionsMap[category] = items
      .map((item) => item.text)
      .filter(Boolean);
    form.categorizedInclusions = inclusionCategories
      .map((entry) => ({
        category: entry.key,
        items: categorizedInclusionsMap[entry.key] || [],
      }))
      .filter((entry) => entry.items.length > 0);
  };

  const updateInclusionPrice = (id: string, e: Event) => {
    const value = (e.target as HTMLInputElement).value;

    form.inclusionPrices[id] = Number(value.replace(/[^0-9.-]+/g, ""));
  };

  const perSQM = computed(() => {
    return !form.basePrice || !form.area
      ? "$0"
      : `${formatPrice(form.basePrice / form.area)}`;
  });
  const onRemoveImage = (type: ImagesHouseTypes, id: string) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      form.mainImages = form.mainImages.filter((x) => x.id !== id);

      if (form.mainImages.length === 0) {
        form.mainImages = [{ id: uuidv4(), url: "" }];
      }

      const mainImageToRender = imagesToRender.value.find(
        (image) => image.type === type
      );

      if (mainImageToRender) {
        mainImageToRender.data = form.mainImages;
      }
    } else {
      setImagesUrl(type, "", id);
    }
  };
  const addNewFeature = () => {
    if (!newFeature.value.trim()) return;

    const feature = {
      name: newFeature.value,
      isSelected: true,
    };

    if (!houseFeatures.value.some((f) => f.name === feature.name)) {
      houseFeatures.value.push(feature);
      form.features.push(feature.name);
    }

    newFeature.value = "";
  };

  const removeFeature = (index: number) => {
    const featureName = houseFeatures.value[index].name;

    houseFeatures.value.splice(index, 1);
    form.features = form.features.filter((f) => f !== featureName);
  };

  const updateFeatureText = (index: number, name: string) => {
    const oldName = houseFeatures.value[index].name;
    const featureIndex = form.features.findIndex((f) => f === oldName);

    if (featureIndex !== -1) {
      form.features[featureIndex] = name;
    }
  };

  const uploadImageRequest = async (formData: FormData) => {
    isUploadingImage.value = true;

    try {
      const res = await store.dispatch("builder/uploadHouseImages", {
        formData,
        builderId: builderId.value,
      });
      const url = res.data[0].value.Location;

      setImagesUrl(imageToUpload.value.type, url, imageToUpload.value.id);
    } catch (error) {
      toast.error("An error occurred while uploading the image");
    } finally {
      isUploadingImage.value = false;
    }
  };

  const validateMultipleImageInput = (
    data: string[],
    type: ImagesHouseTypes,
    url: string
  ) => {
    if (
      data[data.length - 1] === url &&
      validsMultipleImagesInputs.includes(type)
    ) {
      return true;
    }

    return false;
  };

  const addImage = (type: ImagesHouseTypes) => {
    const image = { id: uuidv4(), url: "" };

    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      form.mainImages.push(image);
      const mainImageToRender = imagesToRender.value.find(
        (img) => img.type === type
      );

      if (mainImageToRender) {
        mainImageToRender.data = form.mainImages;
      }

      return;
    }

    if (!form[type].some((i) => i.id === image.id)) {
      form[type].push(image);
    }

    const imageToRender = imagesToRender.value.find((img) => img.type === type);

    if (imageToRender && !imageToRender.data.some((i) => i.id === image.id)) {
      imageToRender.data.push(image);
    }
  };

  const setImagesUrl = (type: ImagesHouseTypes, url: string, id: string) => {
    if (type === ImagesHouseTypes.MAIN_IMAGE) {
      const newImage = { id, url };
      const emptyImageIndex = form.mainImages.findIndex((x) => !x.url);

      if (emptyImageIndex !== -1) {
        form.mainImages[emptyImageIndex] = newImage;
      } else {
        form.mainImages.push(newImage);
      }

      const mainImageToRender = imagesToRender.value.find(
        (image) => image.type === type
      );

      if (mainImageToRender) {
        mainImageToRender.data = form.mainImages;
      }

      return;
    }

    const found = form[type].find((x) => x.id === id);

    if (found) {
      found.url = url;
    }

    const imageToRender = imagesToRender.value
      .find((image) => image.type === type)
      ?.data.find((url) => url.id === id);

    if (imageToRender) {
      imageToRender.url = url;
    }
  };

  const onNameChange = (type: ImagesHouseTypes, id: string, name: string) => {
    form[type].find((el: { id: string }) => el.id === id).name = name;
  };

  const onDescriptionChange = (
    type: ImagesHouseTypes,
    id: string,
    description: string
  ) => {
    form[type].find((el: { id: string }) => el.id === id).description =
      description;
  };

  const onColorChange = (type: ImagesHouseTypes, id: string, color: string) => {
    form[type].find((el: { id: string }) => el.id === id).color = color;
  };
  const onPriceChange = (type: ImagesHouseTypes, id: string, price: string) => {
    form[type].find((el: { id: string }) => el.id === id).price = price;
  };

  const onUploadImage = (
    e: InputEvent,
    type: ImagesHouseTypes,
    id: string
  ): void => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) {
      return;
    }

    imageToUpload.value.id =
      type === ImagesHouseTypes.MAIN_IMAGE ? uuidv4() : id;
    imageToUpload.value.type = type;
    imageToUpload.value.data = "";
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      imageToUpload.value.data = reader.result as string;
      isCropperModalOpen.value = true;
    };
  };

  const onUploadFile = async (e: InputEvent) => {
    if (inclusionName.value.trim() === "") {
      toast.error("Please enter an inclusion name first");
      return;
    }

    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) {
      return;
    }

    const formData = new FormData();

    formData.append("file", files[0]);

    try {
      isUploadingInclusion.value = true;
      await store
        .dispatch("builder/uploadHouseFile", {
          formData,
          builderId: builderId.value,
        })
        .then((res) => {
          form.inclusions.push({
            name: inclusionName.value,
            url: res.data,
            fileName: files[0].name,
          });
          inclusionName.value = "";
        });
    } catch (error) {
      toast.error("An error occurred while uploading the inclusion file");
    } finally {
      isUploadingInclusion.value = false;
    }
  };

  const onInclusionDelete = (url: string) => {
    form.inclusions = form.inclusions.filter((el) => el.url !== url);
  };

  const formatFormFields = () => {
    const types = Object.values(ImagesHouseTypes);
    const formatted = { ...form };

    types.forEach((type) => {
      if (formatted[type] && Array.isArray(formatted[type])) {
        formatted[type].forEach((item) => {
          if (item && typeof item === "object") {
            delete item.id;
          }
        });
      }
    });

    return {
      ...formatted,
      categorizedInclusions: form.categorizedInclusions,
      lounges: form.lounges.map((l) => l.url),
      mainImages: form.mainImages.map((m) => m.url),
      inclusionPack: form.inclusions.map((inclusionId) => ({
        inclusionId,
        price: form.inclusionPrices[inclusionId] || 0,
      })),
      optionalUpgrades: form.optionalUpgrades.map(
        (optionalUpgrade) => optionalUpgrade._id
      ),
      inclusions: undefined,
      inclusionPrices: undefined,
    };
  };

  const validateForm = async () => {
    v$.value.$touch();

    const isValid = await v$.value.$validate();
    const areFacadesErrors = form.facades.length <= 0;
    const hasInvalidInclusionPrices = form.inclusions.some(
      (id) => form.inclusionPrices[id] < 0
    );

    if (!isValid) {
      toast.error("Please fill the required fields");
      return false;
    }

    if (!form.mainImages[0]?.url) {
      toast.error("Please upload a main image");
      return false;
    }

    if (!form[ImagesHouseTypes.FLOORPLANS][0]?.url) {
      toast.error("Please upload a floorplan image");
      return false;
    }

    if (areFacadesErrors) {
      toast.error("Please select at least one facade");
      return false;
    }

    if (hasInvalidInclusionPrices) {
      toast.error("All selected inclusions must have a price greater than 0");
      return false;
    }

    return true;
  };
  const clearForm = () => {
    Object.assign(form, formFields);
    resetVuelidateErrors(v$);
    imagesToRender.value = imagesToRender.value.map((image) => ({
      type: image.type,
      label: image.label,
      data: [],
    }));
  };
  const onCreateHouse = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    loading.value = true;
    const formatedForm = formatFormFields();

    try {
      await store.dispatch("builder/createHouse", {
        ...formatedForm,
        builderId: builderId.value,
        loungeRoom: {
          quantity: formatedForm.loungeRoom,
          urls: formatedForm.lounges,
        },
        bathroom: {
          quantity: formatedForm.bathroom,
          options: formatedForm.bathrooms,
        },
      });
      toast.success("House created successfully");
      clearForm();
      router.push({
        name: "AdminBuilder",
        params: { builderId: builderId.value },
      });
    } catch (error) {
      toast.error("An error occurred while creating the house");
    } finally {
      loading.value = false;
    }
  };

  const getBuilderFacades = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("facade/getFacadesByQuery", {
        builderId: builderId.value,
      });

      facades.value = data;
    } catch (error) {
      toast.error("An error occurred while getting builder");
    } finally {
      loading.value = false;
    }
  };

  const getColorSchemes = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("colorScheme/getColorSchemeByQuery", {
        builderId: builderId.value,
      });

      data.forEach((colorScheme: ColorSchemeInterface) => {
        if (colorScheme.type === ColorSchemeType.EXTERNAL) {
          externalColorSchemes.value.push(
            colorScheme as ExternalColorSchemeInterface
          );
        } else if (colorScheme.type === ColorSchemeType.INTERNAL) {
          internalColorSchemes.value.push(
            colorScheme as InternalColorSchemeInterface
          );
        }
      });
    } catch (error) {
      toast.error("An error occurred while getting builder");
    } finally {
      loading.value = false;
    }
  };

  const getInclusions = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch("inclusion/getInclusionsByQuery", {
        builderId: builderId.value,
      });

      inclusions.value = data;
    } catch (error) {
      toast.error("An error occurred while getting inclusions");
    } finally {
      loading.value = false;
    }
  };

  const getOptionalUpgrades = async () => {
    loading.value = true;

    try {
      const data = await store.dispatch(
        "optionalUpgrade/getOptionalUpgradesByBuilderId",
        {
          builderId: builderId.value,
        }
      );

      optionalUpgrades.value = data;
    } catch (error) {
      toast.error("An error occurred while getting optional upgrades");
    } finally {
      loading.value = false;
    }
  };

  async function onCropImage(file: File) {
    isCropperModalOpen.value = false;
    imageToUpload.value.data = file;
    const formData = new FormData();

    formData.append("files", file);
    uploadImageRequest(formData);
  }

  onMounted(() => {
    getBuilderFacades();
    getColorSchemes();
    getInclusions();
    getOptionalUpgrades();
  });

  watch(
    () => form.storey,
    () => {
      form.facades = [];
    }
  );

const __returned__ = { imageToUpload, isCropperModalOpen, toast, facades, filteredFacades, externalColorSchemes, internalColorSchemes, inclusions, optionalUpgrades, houseFeatures, newFeature, loading, isUploadingImage, isUploadingInclusion, inclusionName, isInternalSchemeModalOpen, isInclusionModalOpen, isOptionalUpgradeModalOpen, getEmptyImage, initMainImage, initFloorplans, initLounges, initKitchens, initBathrooms, imagesToRender, formFields, form, inclusionCategories, categorizedInclusionsMap, rules, v$, builderId, updateFacades, updateExternalColorSchemes, updateInternalColorSchemes, updateInclusions, showUploadButton, updateOptionalUpgrades, handleCreateInternalColorScheme, handleCreateInclusion, handleCreateOptionalUpgrade, updateCategorizedInclusions, updateInclusionPrice, perSQM, onRemoveImage, addNewFeature, removeFeature, updateFeatureText, uploadImageRequest, validateMultipleImageInput, addImage, setImagesUrl, onNameChange, onDescriptionChange, onColorChange, onPriceChange, onUploadImage, onUploadFile, onInclusionDelete, formatFormFields, validateForm, clearForm, onCreateHouse, getBuilderFacades, getColorSchemes, getInclusions, getOptionalUpgrades, onCropImage, ref, reactive, computed, onMounted, watch, get store() { return store }, get router() { return router }, get ImagesHouseTypes() { return ImagesHouseTypes }, get ImageHouseLabels() { return ImageHouseLabels }, get Floorplan() { return Floorplan }, get Inclusions() { return Inclusions }, get HouseFeature() { return HouseFeature }, get ImageToRender() { return ImageToRender }, get useToast() { return useToast }, get required() { return required }, get minValue() { return minValue }, get maxValue() { return maxValue }, get resetVuelidateErrors() { return resetVuelidateErrors }, get useVuelidate() { return useVuelidate }, get VuePictureCropper() { return VuePictureCropper }, get cropper() { return cropper }, ImageCropperModal, get ImageToUpload() { return ImageToUpload }, get validImagesColorInputs() { return validImagesColorInputs }, get validsImagesNameInputs() { return validsImagesNameInputs }, get validImagesDescriptionInputs() { return validImagesDescriptionInputs }, get validsMultipleImagesInputs() { return validsMultipleImagesInputs }, ListItemGenerator, get formatPrice() { return formatPrice }, CheckboxInput, get uuidv4() { return uuidv4 }, get FacadeInterface() { return FacadeInterface }, get formatPriceTrim() { return formatPriceTrim }, get updatePriceInput() { return updatePriceInput }, get AssetGalleryFormType() { return AssetGalleryFormType }, get ColorSchemeInterface() { return ColorSchemeInterface }, get CreateInternalColorSchemeInterface() { return CreateInternalColorSchemeInterface }, get ExternalColorSchemeInterface() { return ExternalColorSchemeInterface }, get InternalColorSchemeInterface() { return InternalColorSchemeInterface }, get ColorSchemeType() { return ColorSchemeType }, get CreateInclusionInterface() { return CreateInclusionInterface }, get InclusionInterface() { return InclusionInterface }, get CreateOptionalUpgradeInterface() { return CreateOptionalUpgradeInterface }, get OptionalUpgrade() { return OptionalUpgrade }, get InclusionCategory() { return InclusionCategory }, AddAndUpdateAssetInGalleryModal, AddAndUpdateInclusionModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})